import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import consoleCapture from '../../utils/consoleCapture'

const ConsoleExporter = ({ position = 'bottom-right', showStats = true }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [stats, setStats] = useState({ total: 0, byLevel: {} })
  const [isCapturing, setIsCapturing] = useState(false)
  const [includeFilter, setIncludeFilter] = useState('')
  const [excludeFilter, setExcludeFilter] = useState('')
  const [filteredCount, setFilteredCount] = useState(0)
  const [copySuccess, setCopySuccess] = useState(false)

  useEffect(() => {
    // Update stats every second when open
    if (isOpen && showStats) {
      const updateStats = () => {
        setStats(consoleCapture.getStats())
        // Update filtered count if filter is active
        if (includeFilter || excludeFilter) {
          const filtered = consoleCapture.searchLogs(includeFilter, excludeFilter)
          setFilteredCount(filtered.length)
        } else {
          setFilteredCount(consoleCapture.getLogs().length)
        }
      }
      updateStats() // Initial update
      const interval = setInterval(updateStats, 1000)
      return () => clearInterval(interval)
    }
  }, [isOpen, showStats, includeFilter, excludeFilter]) // Fixed: No more filterText reference

  useEffect(() => {
    // Status change listener to keep UI in sync
    const handleStatusChange = (isCapturing) => {
      setIsCapturing(isCapturing)
      // Update stats immediately if capturing
      if (isCapturing) {
        setStats(consoleCapture.getStats())
      }
    }

    // Check initial state and add listener
    const initialize = () => {
      const capturing = consoleCapture.isCapturing

      setIsCapturing(capturing)

      // Update stats immediately if capturing
      if (capturing) {
        const stats = consoleCapture.getStats()
        setStats(stats)
      }

      // Add listener for future status changes
      consoleCapture.addStatusChangeListener(handleStatusChange)
    }

    // Check immediately
    initialize()

    // Cleanup function
    return () => {
      consoleCapture.removeStatusChangeListener(handleStatusChange)
    }
  }, [])

  const handleToggleCapture = () => {
    if (isCapturing) {
      consoleCapture.stop()
    } else {
      consoleCapture.start()
    }
    // Note: Don't manually update state - the listener will handle it
  }

  const handleExportText = () => {
    consoleCapture.downloadLogs('text', includeFilter, excludeFilter)
  }

  const handleExportJSON = () => {
    consoleCapture.downloadLogs('json', includeFilter, excludeFilter)
  }

  const handleCopyToClipboard = async () => {
    const success = await consoleCapture.copyToClipboard(includeFilter, excludeFilter)
    if (success) {
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000) // Hide after 2 seconds
    }
  }

  const handleClear = () => {
    if (window.confirm('Are you sure you want to clear all captured logs?')) {
      consoleCapture.clear()
      setStats({ total: 0, byLevel: {} })
    }
  }

  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-20 right-4',
    'top-left': 'top-20 left-4',
  }

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed ${positionClasses[position]} z-50 bg-gray-800 hover:bg-gray-700 text-white rounded-full p-3 shadow-lg transition-all duration-200 ${isOpen ? 'scale-0' : 'scale-100'}`}
        aria-label="Console Exporter"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="4 17 10 11 4 5"></polyline>
          <line x1="12" y1="19" x2="20" y2="19"></line>
        </svg>
      </button>

      {/* Panel */}
      {isOpen && (
        <div
          className={`fixed ${positionClasses[position]} z-50 bg-gray-900 text-white rounded-lg shadow-xl p-4 min-w-[300px] transition-all duration-200`}
        >
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Console Exporter</h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-white"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Capture Status */}
          <div className="mb-4 p-2 bg-gray-800 rounded">
            <div className="flex items-center justify-between">
              <span className="text-sm">
                Status:{' '}
                {isCapturing ? (
                  <span className="text-green-400">Capturing</span>
                ) : (
                  <span className="text-gray-400">Stopped</span>
                )}
              </span>
              <button
                onClick={handleToggleCapture}
                className={`px-3 py-1 rounded text-xs ${
                  isCapturing ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {isCapturing ? 'Stop' : 'Start'}
              </button>
            </div>
          </div>

          {/* Stats */}
          {showStats && (
            <div className="mb-4 p-2 bg-gray-800 rounded">
              <div className="text-sm">
                <div className="mb-1">Total Logs: {stats.total}</div>
                <div className="space-y-1">
                  {Object.entries(stats.byLevel).map(([level, count]) => (
                    <div key={level} className="flex justify-between">
                      <span className="capitalize">{level}:</span>
                      <span>{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Filter Inputs */}
          <div className="mb-4 space-y-3">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Include patterns:</label>
              <div className="relative">
                <input
                  type="text"
                  value={includeFilter}
                  onChange={(e) => setIncludeFilter(e.target.value)}
                  placeholder="e.g., [AUTH], [CHAT] or error | warning"
                  className="w-full px-3 py-2 pr-8 bg-gray-800 text-white rounded border border-gray-700 focus:border-green-500 focus:outline-none text-sm"
                />
                {includeFilter && (
                  <button
                    onClick={() => setIncludeFilter('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    aria-label="Clear include filter"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Show logs matching any of these patterns (comma or | separated)
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Exclude patterns:</label>
              <div className="relative">
                <input
                  type="text"
                  value={excludeFilter}
                  onChange={(e) => setExcludeFilter(e.target.value)}
                  placeholder="e.g., debug, trace, system"
                  className="w-full px-3 py-2 pr-8 bg-gray-800 text-white rounded border border-gray-700 focus:border-red-500 focus:outline-none text-sm"
                />
                {excludeFilter && (
                  <button
                    onClick={() => setExcludeFilter('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    aria-label="Clear exclude filter"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Hide logs matching any of these patterns
              </div>
            </div>

            {(includeFilter || excludeFilter) && (
              <div className="mt-2 p-2 bg-gray-800 rounded text-xs text-gray-400">
                Showing {filteredCount} of {stats.total} logs
              </div>
            )}
          </div>

          {/* Export Buttons */}
          <div className="space-y-2">
            <button
              onClick={handleCopyToClipboard}
              className={`w-full px-4 py-2 rounded text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                copySuccess
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-purple-600 hover:bg-purple-700'
              }`}
            >
              {copySuccess ? (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Copied to Clipboard!
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  Copy for Claude
                </>
              )}
            </button>
            <button
              onClick={handleExportText}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors"
            >
              Export as Text
            </button>
            <button
              onClick={handleExportJSON}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors"
            >
              Export as JSON
            </button>
            <button
              onClick={handleClear}
              className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm transition-colors"
            >
              Clear Logs
            </button>
          </div>

          {/* Keyboard Shortcuts Info */}
          <div className="mt-4 pt-4 border-t border-gray-700 text-xs text-gray-400">
            <div>Console commands:</div>
            <div className="font-mono mt-1">
              <div>window.consoleCapture.downloadLogs()</div>
              <div>window.consoleCapture.getStats()</div>
              <div>window.consoleCapture.searchLogs(&apos;include&apos;, &apos;exclude&apos;)</div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

ConsoleExporter.propTypes = {
  position: PropTypes.oneOf(['bottom-right', 'bottom-left', 'top-right', 'top-left']),
  showStats: PropTypes.bool,
}

export default ConsoleExporter
